import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Skeleton } from "@mui/material";
import _ from "lodash";

import { AccessTimeIcon, DescriptionOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Autocomplete from "components/Autocomplete";
import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import Tooltip from "components/Tooltip";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import {
  guessedConnectionsSubmit,
  useOnboardingCompanyPackageCategorizedFileFieldCounts,
} from "entities/Deal/sdk";
import {
  useDealDetails,
  useFilesWithNoConnectionToCustomFieldValue,
  useOnboardingChecklistFileFields,
} from "entities/Deal/sdk";
import { useChecklistFileImportReady } from "entities/TableStorage/hooks";

import { extractCustomFieldPaths } from "./utils";

export interface ICategorizationDialog {
  open: boolean;
  onClose: () => void;
  isCategorizing: boolean;
  checklistFileImportId: number | null;
  handlePackageUpdate: ({ message }: { message?: string }) => void;
}

const CategorizationDialog = ({
  open,
  onClose,
  isCategorizing,
  checklistFileImportId,
  handlePackageUpdate,
}: ICategorizationDialog) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();
  const dealId = Number(params.dealId);
  const { data: deal } = useDealDetails({ dealId });
  const [values, setValues] = useState<
    Array<{
      connectionId: number;
      value: number | null;
    }>
  >([]);
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const { mutate: refetchOnboardingCompanyPackageCategorizedFileFieldCounts } =
    useOnboardingCompanyPackageCategorizedFileFieldCounts({
      dealId,
    });

  const { data: files, mutate: mutateFiles } =
    useFilesWithNoConnectionToCustomFieldValue({
      dealId,
      enabled: open && !isCategorizing,
      checklistFileImportId,
    });

  const refetchFiles = useCallback(() => {
    mutateFiles();
  }, [mutateFiles]);

  const { data: customFields } = useOnboardingChecklistFileFields({
    dealId,
    enabled: open && !isCategorizing,
  });

  const options = useMemo(
    () => extractCustomFieldPaths({ data: customFields || [] }),
    [customFields]
  );

  const valueForConnectionRetrieve = useCallback(
    ({
      connection,
    }: {
      connection: {
        field_id: number | null;
        id: number;
      };
    }) =>
      options.find(
        (option) =>
          option.value ===
          values.find((value) => value.connectionId === connection.id)?.value
      ) || options[0],
    [options, values]
  );

  useEffect(() => {
    if (open && !_.isNil(files)) {
      setValues(
        files
          .filter((file) => !_.isNil(file.guessed_connection))
          .map((file) => ({
            connectionId: file.guessed_connection!.id,
            value: file.guessed_connection!.field_id,
          }))
      );

      return () => setValues([]);
    }
  }, [open, setValues, files]);

  const handleSubmit = useCallback(() => {
    showLoading();

    const formattedValues = values.map((value) => ({
      id: value.connectionId,
      field_id: [0, -1, null].includes(value.value) ? null : value.value,
      should_mark_as_na: value.value === -1,
    }));

    guessedConnectionsSubmit({
      dealId,
      connections: formattedValues,
    })
      .then(() => {
        onClose();
        handlePackageUpdate({
          message: "Files have been categorized successfully!",
        });
        refetchOnboardingCompanyPackageCategorizedFileFieldCounts();
      })
      .finally(() => hideLoading());
  }, [
    dealId,
    values,
    showLoading,
    hideLoading,
    onClose,
    handlePackageUpdate,
    refetchOnboardingCompanyPackageCategorizedFileFieldCounts,
  ]);

  useChecklistFileImportReady({
    successEventHandler: () => refetchFiles(),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          margin: theme.spacing(2, 3, 0, 3),
          padding: 2,
          borderRadius: theme.spacing(1),
          backgroundColor: colors.blue20,
        }}
        flexDirection="row"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="h1" fontWeight={700}>
          Categorize documents
        </Text>
        <Text noWrap variant="text2" fontWeight={700} maxWidth="190px">
          {deal?.name}
        </Text>
      </DialogTitle>

      <DialogContent sx={{ height: "350px", paddingTop: 4 }}>
        {isCategorizing && (
          <Stack
            flexDirection="row"
            padding={theme.spacing(4, 0)}
            alignItems="center"
          >
            <AccessTimeIcon sx={{ width: "64px", height: "64px" }} />
            <Text
              variant="text1"
              maxWidth="485px"
              padding={theme.spacing(0, 3)}
            >
              The uploaded documents are being categorized. It usually takes
              less than a minute...
            </Text>
          </Stack>
        )}

        {!_.isEmpty(files) && (
          <Stack
            direction="column"
            padding={theme.spacing(1, 0)}
            sx={{
              animation: "appear 0.3s linear",
              "@keyframes appear": {
                from: {
                  opacity: 0,
                },
                to: { opacity: 1 },
              },
            }}
          >
            <Stack
              spacing={2}
              padding={theme.spacing(3, 0)}
              paddingRight={2}
              sx={{
                overflowY: "auto",
              }}
            >
              <Stack flexDirection="row">
                <Text
                  variant="text4"
                  fontWeight={700}
                  color={colors.gray60}
                  sx={{ flexBasis: "50%" }}
                >
                  {files?.length} UPLOADED DOCUMENT
                  {files?.length === 1 ? "" : "S"}
                </Text>

                <Text
                  variant="text4"
                  fontWeight={700}
                  color={colors.gray60}
                  sx={{ flexBasis: "50%" }}
                >
                  CATEGORY
                </Text>
              </Stack>

              {files?.map((file) => (
                <Stack gap={1} flexDirection="row" key={file.id}>
                  <Stack justifyContent="center" width="50%" maxWidth={405}>
                    <Tooltip title={file.original_file_name} enterDelay={1000}>
                      <Stack flexDirection="row" gap={1} alignItems="center">
                        <DescriptionOutlinedIcon sx={{ height: "25px" }} />
                        <Text textOverflow="ellipsis" overflow="hidden" noWrap>
                          {file.original_file_name}
                        </Text>
                      </Stack>
                    </Tooltip>
                  </Stack>
                  {!_.isNil(file.guessed_connection) &&
                    !_.isNil(customFields) && (
                      <Stack flexBasis="50%">
                        <Autocomplete
                          value={valueForConnectionRetrieve({
                            connection: file.guessed_connection,
                          })}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                _.isNil(file.guessed_connection?.field_id)
                                  ? "No file will be uploaded"
                                  : ""
                              }
                              sx={{
                                backgroundColor: colors.blue5,
                                borderColor: colors.blue10,
                              }}
                            />
                          )}
                          renderOption={(params, option) => (
                            <li {...params} key={option.value}>
                              {option.label}
                            </li>
                          )}
                          options={options}
                          disableClearable
                          onChange={(e, option) => {
                            setValues((prev) =>
                              prev.map((value) =>
                                value.connectionId ===
                                file.guessed_connection?.id
                                  ? {
                                      connectionId: file.guessed_connection.id,
                                      value: Number(option?.value),
                                    }
                                  : value
                              )
                            );
                          }}
                        />
                      </Stack>
                    )}
                  {(_.isNil(customFields) ||
                    (_.isNil(file.guessed_connection) &&
                      _.isNil(customFields))) && (
                    <Stack flexBasis="50%">
                      <Skeleton
                        variant="rectangular"
                        width="418px"
                        height={56}
                        sx={{ borderRadius: 1 }}
                      />
                    </Stack>
                  )}
                  {_.isNil(file.guessed_connection) &&
                    !_.isNil(customFields) && (
                      <Stack flexBasis="50%">
                        <TextField
                          value="File is still being processed."
                          disabled
                          fullWidth
                          sx={{ backgroundColor: colors.blue5 }}
                        />
                      </Stack>
                    )}
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isCategorizing || _.isEmpty(values)}
        >
          Save categories
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategorizationDialog;
